export interface OrderActionMeta { node: string; roles: string[]; description: string }

/** Non-workflow placements: the fixed bottom bar (left / right) and row-level links that stay beside their record. */
export const TOOLBAR_NODE = 'toolbar'
export const TOOLBAR_END_NODE = 'toolbar-end'
export const INLINE_NODE = 'inline'

const action = (node: string, roles: string[], description: string): OrderActionMeta => ({ node, roles, description })
export const ORDER_ACTIONS: Record<string, OrderActionMeta> = {
  assign: action('assign', ['MARKET'], '将当前订单交给指定客服继续安排，分配范围沿用当前业务规则。'),
  dispatch: action('dispatch', ['SERVICE'], '为选定设备安排工程师和检测时间，提交后进入对应检测任务。'),
  receive: action('receive', ['SERVICE'], '核对到件设备后登记收件，再按原流程安排检测。'),
  externalDetails: action(TOOLBAR_NODE, ['MARKET', 'SERVICE', 'FINANCE'], '收发客服可维护代送单位和外部联系人；分包成本仍由负责业务员或管理员维护。'),
  externalInspect: action('inspection', ['SERVICE'], '逐台登记外部返回的真实检测结果，再按原流程上传证书。'),
  externalDispatch: action('dispatch', ['SERVICE'], '确认整单交由已登记的外部检测方办理，保留设备与承接记录。'),
  inspect: action('inspection', ['ENGINEER', 'LAB_ENGINEER'], '打开本订单的检测办理页面，按原要求录入、确认并提交检测结果。'),
  editOrder: action(TOOLBAR_NODE, ['MARKET', 'SERVICE'], '修改当前订单允许维护的业务资料，保存后更新订单信息。'),
  returnDevice: action('inspection', ['ENGINEER', 'SERVICE'], '按原规则退回选定设备，保留检测及操作记录。'),
  redispatchDevice: action('inspection', ['SERVICE'], '为已退回的设备重新安排检测，保留原派单记录。'),
  uploadCertificate: action('certificate', ['SERVICE'], '为选定设备上传证书，补齐证书覆盖；付款、核销和交付仍按原规则办理。'),
  confirmCertificate: action('certificate', ['SERVICE'], '核对全部设备的证书覆盖后，按原规则确认证书。'),
  downloadCertificate: action(INLINE_NODE, [], '下载当前有权限访问的有效证书文件。'),
  generateBill: action('invoice_application', ['SERVICE', 'FINANCE'], '根据当前检测结果生成缴费单，继续按原计费和审批规则办理。'),
  modifyBill: action(TOOLBAR_END_NODE, ['FINANCE', 'MARKET'], '调整当前允许修改的缴费单金额，保留原金额校验和操作记录；申请开票后费用冻结。'),
  applyInvoice: action('invoice_application', ['MARKET', 'CUSTOMER'], '核对开票主体、金额和资料后提交，后续按现有审批与开票流程办理。'),
  previewConsolidatedInvoice: action('invoice_application', ['FINANCE'], '预览同公司、同业务员的订单组合，由归属业务员确认并提交后按原财务流程开票。'),
  reviewInvoice: action('invoice_application', ['MARKET'], '核对本次开票申请资料与金额，按原审批任务通过或退回补正。'),
  issueInvoice: action('invoice_issue', ['FINANCE'], '为符合开票条件的申请登记发票信息并上传发票文件。'),
  viewInvoice: action(INLINE_NODE, [], '查看当前有权限访问的发票及文件。'),
  viewPayment: action(INLINE_NODE, ['CUSTOMER'], '查看自己的付款记录与审核结果。'),
  submitPayment: action('customer_payment', ['CUSTOMER'], '按原支付方式办理本订单付款；提交凭证后仍须财务核实。'),
  submitVoucherForCustomer: action('customer_payment', ['MARKET'], '负责业务员或管理员为选定缴费单上传付款凭证图片或 PDF；链接仅可补充，提交后由财务核实。'),
  auditVoucher: action('customer_payment', ['FINANCE'], '查看付款凭证并结合实际到账按原规则审核，凭证本身不代表已确认到账。'),
  recordInvoiceReceipt: action('customer_payment', ['FINANCE'], '核对实际银行流水后登记本笔回款，后续仍需市场认领和财务核销。'),
  recordDirectReceipt: action('customer_payment', ['FINANCE'], '为明确不开票的有效缴费单登记真实到账，继续按原不开票结算规则办理。'),
  auditMonthly: action('customer_payment', ['MONTHLY_ADMIN'], '审核本公司允许审核的月结事项，通过表示取得原月结依据，不代表收到现金。'),
  reviewRefund: action('customer_payment', ['MARKET'], '审核本人或角色共享池中的退款申请，沿用原审批门禁。'),
  auditRefund: action('customer_payment', ['FINANCE'], '审核已满足原市场审批门禁的退款申请，沿用原退款处理表单。'),
  reviewReissue: action('invoice_issue', ['MARKET'], '审核原换票申请及资料，审核通过后仍按原流程由财务办理。'),
  refund: action('customer_payment', ['CUSTOMER', 'FINANCE', 'MARKET'], '对选定单据按原退款申请与审批流程办理，保留原退款条件。'),
  claimPayment: action('finance_settlement', ['MARKET'], '核对本笔回款的客户和开票主体，认领通过后交财务核销；不符时按原规则驳回。'),
  reconcilePayment: action('finance_settlement', ['FINANCE'], '核实本笔回款及对应票据后确认核销；订单是否完成仍由全部原完成条件判断。'),
  mailReturn: action('completion', ['SERVICE'], '满足现有交付条件后登记设备寄回；不增加新的结算前置条件。'),
  cancelOrder: action(TOOLBAR_END_NODE, ['MARKET', 'SERVICE', 'CUSTOMER'], '按当前角色及订单状态取消订单，继续保留原取消条件，提交前需再次确认。'),
  submitOrder: action('submit', ['CUSTOMER'], '提交当前草稿订单，进入原有分配和检测安排流程。'),
  sendTracking: action('assign', ['CUSTOMER'], '登记客户寄出设备的快递信息，供客服核对收件。'),
  exception: action(TOOLBAR_END_NODE, ['MARKET'], '按原规则处理本订单异常，保留异常原因与办理结果。'),
  export: action(TOOLBAR_NODE, [], '导出当前有权限查看的订单资料，每个设备一行。'),
  timeline: action(TOOLBAR_NODE, ['ADMIN'], '按现有管理权限修正节点时间并保留调整记录。'),
}

/** Empty role metadata denotes a read action; its caller must still check resource access. */
export function actionMatchesRoles(key: string, roles: string[]): boolean {
  const meta = ORDER_ACTIONS[key]
  return !!roles.length && !!meta && (roles.includes('ADMIN') || !meta.roles.length || meta.roles.some(role => roles.includes(role)))
}

export interface ActionTarget { id: string; type?: string; label?: string; amount?: string }

/** Business wording for the object an action works on; internal record IDs are never shown. */
export function targetSubject(target: ActionTarget, index: number, total: number): string | undefined {
  const seq = total > 1 ? String(index + 1) : ''
  const money = (value?: string) => (value && Number.isFinite(Number(value)) ? ` · ¥${Number(value).toFixed(2)}` : '')
  const label = target.label?.trim()
  switch (target.type) {
    case 'invoice':
      if (label && label !== target.id && !label.startsWith('发票申请')) return label.startsWith('发票') ? label : `发票 ${label}`
      return `发票申请（未开票）${seq}`
    case 'bill':
      return label ? `${label.startsWith('缴费单') ? label : `缴费单 ${label}`}${money(target.amount)}` : undefined
    case 'payment':
      return `待审核付款凭证${seq}`
    case 'refund': {
      const amount = label?.match(/¥\s*([\d.]+)/)?.[1]
      return `退款申请${seq}${money(amount)}`
    }
    case 'approval':
      return `${label?.split(/\s+/)[0] || '审批事项'}${seq}`
    default:
      return undefined
  }
}
